import React from 'react';
import LegalPageLayout, { Section, Paragraph, List } from '../../components/LegalPageLayout';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight, Shield, Users, FileText, Target } from 'lucide-react';

const About = () => {
    return (
        <LegalPageLayout title="About ClientCheck" lastUpdated={null}>
            <Section title="Our Mission">
                <Paragraph>
                    ClientCheck was created to help contractors document job experiences in a professional 
                    and structured way. We believe that sharing factual information about job experiences 
                    benefits the entire contractor community.
                </Paragraph>
                <Paragraph>
                    Too often, contractors accept jobs without any way to know what to expect. 
                    ClientCheck changes that by providing a platform where contractors can share 
                    their experiences and learn from each other.
                </Paragraph>
            </Section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
                <div className="p-4 bg-card border border-border rounded-lg">
                    <Shield className="h-8 w-8 text-primary mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">Professional Documentation</h3>
                    <p className="text-sm text-muted-foreground">
                        Structured ratings and factual reporting, not public accusations or personal attacks.
                    </p>
                </div>
                <div className="p-4 bg-card border border-border rounded-lg">
                    <Users className="h-8 w-8 text-primary mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">Contractor Community</h3>
                    <p className="text-sm text-muted-foreground">
                        Built by and for contractors who want to make smarter business decisions.
                    </p>
                </div>
                <div className="p-4 bg-card border border-border rounded-lg">
                    <FileText className="h-8 w-8 text-primary mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">Address-Based Reviews</h3>
                    <p className="text-sm text-muted-foreground">
                        Reviews are tied to job addresses, not individuals, protecting privacy while sharing useful information.
                    </p>
                </div>
                <div className="p-4 bg-card border border-border rounded-lg">
                    <Target className="h-8 w-8 text-primary mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">Better Decisions</h3>
                    <p className="text-sm text-muted-foreground">
                        Know what to expect before you accept a job, based on real contractor experiences.
                    </p>
                </div>
            </div>

            <Section title="What We Believe">
                <List items={[
                    "Contractors deserve access to shared professional knowledge",
                    "Factual documentation is more valuable than emotional venting",
                    "Privacy matters—reviews should focus on addresses, not individuals",
                    "Fair systems account for context and circumstances",
                    "Professional standards benefit everyone in the community"
                ]} />
            </Section>

            <Section title="How It Works">
                <Paragraph>
                    ClientCheck allows contractors to search for job addresses and see aggregated 
                    experience summaries based on reviews from other contractors. When you complete a job, 
                    you can submit a structured review that covers:
                </Paragraph>
                <List items={[
                    "Payment timeliness and reliability",
                    "Communication quality and responsiveness",
                    "Professional conduct",
                    "Schedule adherence",
                    "Fair handling of change orders",
                    "Jobsite access and cooperation"
                ]} />
                <Paragraph>
                    Our system calculates experience summaries that help contractors make informed decisions 
                    about accepting work at specific addresses.
                </Paragraph>
            </Section>

            <Section title="Our Commitment">
                <Paragraph>
                    ClientCheck is committed to:
                </Paragraph>
                <List items={[
                    "Maintaining a professional, respectful platform",
                    "Protecting user privacy and data security",
                    "Fair and transparent moderation practices",
                    "Continuous improvement based on user feedback",
                    "Supporting the contractor community"
                ]} />
            </Section>

            <div className="mt-8 p-6 bg-primary/5 border border-primary/20 rounded-lg text-center">
                <h3 className="font-semibold text-foreground mb-2">Ready to Get Started?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                    Join the contractor community and start making smarter business decisions.
                </p>
                <Link to="/signup">
                    <Button>
                        Create Free Account
                        <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </Link>
            </div>
        </LegalPageLayout>
    );
};

export default About;
